'use client';

import { AlertBox, ButtonBase, CopyToClipboard } from '../../../../common';
import { css, hstack, vstack } from '@bts-web/utils-style-engine';
import { SavingsPlanCancelModal } from '../SavingsPlanCancelModal/SavingsPlanCancelModal';
import { decodeBase64String } from '@bts-web/utils-functions';
import { SavingsPlanStatus } from '@bts-web/data-layer/server';
import { useModalDrawerContext } from '../../../../common/components/ModalDrawerContext/ModalDrawerContext';
import { RegularInfo } from '../../../../common/svgIcons';

export const SavingsPlanDetailsFooter = ({
  translations,
  savingsPlan,
  isConfirmedTransaction,
}: {
  translations: {
    cancelPlan: string;
    resumePlan: string;
    goBack: string;
    pid: string;
    orderId: string;
    savingsPlansPidCopySuccessMessage: string;
    savingsPlanPidCopyFailedMessage: string;
    cancelPlanConfirmationTitle: string;
    cancelPlanConfirmationDescription: string;
    cancelPlanInProgressConfirmationDescription: string;
    savingsPlanInProgressTitle: string;
    savingsPlanInProgressDescription: string;
  };
  savingsPlan: {
    id: string;
    name: string;
    status: SavingsPlanStatus;
  };
  isConfirmedTransaction?: boolean;
}) => {
  const { showModal } = useModalDrawerContext();

  const showCancelDrawer = () => {
    showModal(SavingsPlanCancelModal, {
      translations,
      savingsPlan,
      disableOverlayClose: true,
      isConfirmedTransaction,
    });
  };

  const decodedSavingsPlanId =
    savingsPlan?.id && decodeBase64String(savingsPlan?.id).split(':')[1];

  const pid = savingsPlan?.id && (
    <div
      className={hstack({
        overflow: 'hidden',
        fontSize: 'label.medium_medium',
        justifyContent: 'space-between',
        flex: 'auto',
        width: '100%',
      })}
    >
      <div
        role="presentation"
        className={css({
          flex: 1,
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          minWidth: 0,
          color: 'neutrals.text_secondary',
          fontSize: 'caption.small',
          fontWeight: 'caption.small',
          lineHeight: 'caption.small',
          letterSpacing: 'caption.small',
        })}
      >
        {translations.orderId} {decodedSavingsPlanId}
      </div>

      <CopyToClipboard
        value={decodedSavingsPlanId as string}
        translations={{
          successMessage: translations.savingsPlansPidCopySuccessMessage,
          failedMessage: translations.savingsPlanPidCopyFailedMessage,
        }}
      />
    </div>
  );

  return (
    <div
      className={css({
        width: '100%',
        marginTop: 'auto',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        gap: 'extra_small_2',
        px: 'medium',
        mb: 'medium',
      })}
      aria-label="transaction-details-footer"
    >
      {pid}

      <div
        className={vstack({
          gap: 'medium',
          mt: 'auto',
        })}
      >
        {isConfirmedTransaction && (
          <AlertBox
            variant="info"
            className={css({
              width: '100%',
            })}
          >
            <div
              className={hstack({
                gap: 'extra_small_2',
                alignItems: 'flex-start',
              })}
            >
              <RegularInfo
                size={20}
                color="information.text_primary"
                className={css({ flexShrink: 0 })}
              />
              <div
                className={vstack({
                  gap: 'extra_small_2',
                  alignItems: 'flex-start',
                })}
              >
                <span
                  className={css({
                    fontSize: 'label.medium_medium',
                    fontWeight: 'label.medium_medium',
                    lineHeight: 'label.medium_medium',
                    letterSpacing: 'label.medium_medium',
                    color: 'information.text_primary',
                  })}
                >
                  {translations.savingsPlanInProgressTitle}
                </span>
                <span
                  className={css({
                    fontSize: 'body.medium',
                    fontWeight: 'body.medium',
                    lineHeight: 'body.medium',
                    letterSpacing: 'body.medium',
                    color: 'neutrals.text_primary',
                  })}
                >
                  {translations.savingsPlanInProgressDescription}
                </span>
              </div>
            </div>
          </AlertBox>
        )}

        {savingsPlan?.status === 'ACTIVE' && (
          <div
            className={css({
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
            })}
          >
            <ButtonBase onClick={showCancelDrawer} visual="secondary">
              {translations.cancelPlan}
            </ButtonBase>
          </div>
        )}
      </div>
    </div>
  );
};
